import { Paper, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import React, { useState } from 'react';
import { Box, Container } from '@mui/system';
import { accessServiceTimeLineObj } from 'assets/animation/portalAnimation';
import { ISystemSettingModel } from 'models/setting';
import { useSelector } from 'react-redux';
import { IApplicationState } from 'store/state/app-state';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    // Dynamic class logic
    const extraClass = value === 0 ? 'b-top-left-0' : value === 4 ? 'b-bottom-left-0' : '';

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
            style={{ flex: 1 }}
            className={`cus-tab-panel ${extraClass}`}
        >
            {value === index && (
                <Box>
                    <Typography component="div">{children}</Typography>
                </Box>
            )}
        </div>
    );
}

const AccessService: React.FC = () => {
    const setting: ISystemSettingModel = useSelector((state: IApplicationState) => state?.Setting);

    const accessServiceTimeLine = React.useRef(null);

    // Tab State
    const [value, setValue] = useState(0);

    React.useEffect(() => {
        accessServiceTimeLineObj(accessServiceTimeLine);
    }, []);

    const isMobile = useMediaQuery('(max-width:767px)');

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    return (
        <section className="access-service" ref={accessServiceTimeLine} id="access-service">
            <Container maxWidth="lg">
                <Typography variant="h3" className="section-title">
                    How To Access Service Though Healthreach
                </Typography>
                <Paper elevation={0} className="service-tab-paper">
                    {/* Left Side Tabs */}
                    <Tabs
                        orientation={isMobile ? 'horizontal' : 'vertical'}
                        value={value}
                        onChange={handleTabChange}
                        TabIndicatorProps={{ hidden: true }}
                    >
                        <Tab label="Appointment with Psychologist" disableRipple />
                        <Tab label="Appointment with Psychiatrist" disableRipple />
                        <Tab label="Medicolegal" disableRipple />
                        <Tab label="ADHD assessment as Adult" disableRipple />
                        <Tab label="ADHD assessment as Child" disableRipple />
                    </Tabs>

                    <TabPanel value={value} index={0}>
                        <Typography variant="h6" className="panel-title">
                            How to get an appointment with
                            <Typography component="span" color="primary" fontWeight={600}>
                                Psychologist / Social Worker / Occupational Therapist
                            </Typography>
                        </Typography>

                        <ul>
                            <li>
                                You will need to complete a Mental Health Care Plan (MHCP) with your GP and obtain a
                                referral from your GP.
                            </li>
                            <li>
                                Choose between bulk bill (only if in remote/regional region) or gap fee for quicker
                                access.
                            </li>
                            <li>
                                You may choose your choice of clinician or we can select the first available one for
                                you.
                            </li>
                        </ul>
                    </TabPanel>

                    <TabPanel value={value} index={1}>
                        <Typography variant="h6" className="panel-title">
                            How to get an appointment with
                            <Typography component="span" color="primary" fontWeight={600}>
                                Psychiatrist
                            </Typography>
                        </Typography>
                        <ul>
                            <li>You will need a referral from your GP to access a psychiatrist from Heachreach.</li>
                            <li>
                                Choose between bulk bill (only if in remote/regional region) or gap fee for quicker
                                access.
                            </li>
                            <li>
                                You may choose your choice of clinician or we can select the first available one for
                                you.
                            </li>
                        </ul>
                    </TabPanel>

                    <TabPanel value={value} index={2}>
                        <Typography variant="h6" className="panel-title">
                            How to get Medicolegal Assessment
                            <Typography component="span" color="primary" fontWeight={600}>
                                Medicolegal
                            </Typography>
                        </Typography>
                        <ul>
                            <li>GP referral not required, please approach us for most suitable clinician and fees.</li>
                        </ul>
                    </TabPanel>

                    <TabPanel value={value} index={3}>
                        <Typography variant="h6" className="panel-title">
                            How to get ADHD assessment
                            <Typography component="span" color="primary" fontWeight={600}>
                                ADHD assessment as an adult
                            </Typography>
                        </Typography>
                        <ul>
                            <li>
                                You will need a referral from your GP to access a psychiatrist from Heachreach for ADHD
                                assessment.
                            </li>
                            <li>
                                Choose between bulk bill (only if in remote/regional region) or gap fee for quicker
                                access.
                            </li>
                            <li>
                                You may choose your choice of clinician or we can select the first available one for
                                you.
                            </li>
                            <li>
                                Provide school reports/collateral sources from 2 different socio-occupational domains
                                including one from childhood.
                            </li>
                            <li>
                                We will assess for ADHD diagnosis or for any alternative explanations. There will be a
                                period of optimisation of medication dose.
                            </li>
                            <li>Back to GP for prescription and regular reviews.</li>
                        </ul>
                    </TabPanel>

                    <TabPanel value={value} index={4}>
                        <Typography variant="h6" className="panel-title">
                            How to get ADHD assessment
                            <Typography component="span" color="primary" fontWeight={600}>
                                ADHD assessment as a child
                            </Typography>
                        </Typography>
                        <ul>
                            <li>
                                You will need a referral from your GP to access a psychiatrist from Heachreach for ADHD
                                assessment.
                            </li>
                            <li>
                                Choose between bulk bill (only if in remote/regional region) or gap fee for quicker
                                access.
                            </li>
                            <li>
                                You may choose your choice of clinician or we can select the first available one for
                                you.
                            </li>
                            <li>
                                Provide school reports/collateral sources from 2 different socio-occupational domains
                                and completion of rating scales.
                            </li>
                            <li>
                                We will assess for ADHD diagnosis or for any alternative explanations. There will be a
                                period of optimisation of medication dose.
                            </li>
                            <li>Back to GP for prescription and regular reviews.</li>
                        </ul>
                    </TabPanel>
                </Paper>
                <Typography variant="body1" className="referral-info">
                    Please send all referrals to{' '}
                    <Typography component="a" color="primary" href={`mailto:${setting?.contacT_US_EMAIL}`}>
                        {setting?.contacT_US_EMAIL}
                    </Typography>{' '}
                    or via Medical Objects
                </Typography>
            </Container>
        </section>
    );
};

export default React.memo(AccessService);
